/**
 * Nasomin bundle — multi-tier bundle add-to-cart logic.
 *
 * Each tier holds one or more [data-stickyglue-item] elements (1 for
 * tier 1, up to 2 for tiers 2 and 3) carrying a variant ID and an
 * optional selling plan ID. On add-to-cart, every item in the active
 * tier becomes a separate line. The "Subscribe & save" toggle is
 * default-on and always selectable; when on, items whose product
 * has a selling plan get that plan applied (items without a plan
 * are added at full price).
 */
(function () {
  'use strict';

  const state = { tier: 1, subscribe: true };
  let root = null;

  function getActiveTierEl() {
    if (!root) return null;
    return root.querySelector('.stickyglue-tier[data-tier="' + state.tier + '"]');
  }

  function renderTierSelection() {
    if (!root) return;
    root.querySelectorAll('.stickyglue-tier').forEach(function (tierEl) {
      const t = parseInt(tierEl.dataset.tier, 10);
      const isActive = t === state.tier;
      tierEl.classList.toggle('is-active', isActive);
      const radio = tierEl.querySelector('input.stickyglue-tier__radio');
      if (radio) radio.checked = isActive;
    });
  }

  function attachTierListeners() {
    root.querySelectorAll('input.stickyglue-tier__radio').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        state.tier = parseInt(radio.value, 10);
        renderTierSelection();
      });
    });
  }

  function syncSubscribeClass() {
    if (!root) return;
    root.classList.toggle('stickyglue-bundle--is-subscribe', !!state.subscribe);
  }

  function attachSubscribeListener() {
    const cb = root.querySelector('[data-stickyglue-subscribe]');
    if (!cb) return;
    cb.addEventListener('change', function () {
      state.subscribe = !!cb.checked;
      syncSubscribeClass();
    });
  }

  function genGroupId() {
    return 'nb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function buildItemsForCurrentTier() {
    const tierEl = getActiveTierEl();
    if (!tierEl) throw new Error('Selected tier not found.');
    const mode = state.subscribe ? 'sub' : 'regular';
    let itemEls = tierEl.querySelectorAll('[data-stickyglue-item][data-mode="' + mode + '"]');
    if (mode === 'sub' && itemEls.length === 0) {
      itemEls = tierEl.querySelectorAll('[data-stickyglue-item][data-mode="regular"]');
    }

    const groupId = genGroupId();
    const items = [];
    itemEls.forEach(function (el) {
      const variantId = parseInt(el.dataset.variantId, 10);
      if (!variantId) return;
      const quantity = parseInt(el.dataset.quantity, 10) || 1;
      const planId = parseInt(el.dataset.sellingPlanId, 10);
      const role = el.dataset.role;

      const item = { id: variantId, quantity: quantity };
      if (state.subscribe && planId) item.selling_plan = planId;

      if (role === 'parent' || role === 'gift') {
        item.properties = {
          _bundle_group_id: groupId,
          _bundle_role: role === 'gift' ? 'free' : 'parent',
        };
        if (role === 'gift') {
          item.properties.Offer = '🎁 FREE GIFT';
        }
      }

      items.push(item);
    });
    if (!items.length) {
      throw new Error('No products are set for this option. Open the theme editor and pick at least one product.');
    }
    return items;
  }

  function showError(msg) {
    const el = root && root.querySelector('[data-stickyglue-error]');
    if (!el) return;
    el.textContent = msg || 'Something went wrong adding to cart.';
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 5000);
  }

  function setBusy(busy) {
    const btn = root && root.querySelector('[data-stickyglue-atc]');
    if (!btn) return;
    btn.disabled = !!busy;
    btn.classList.toggle('is-loading', !!busy);
    const lbl = btn.querySelector('[data-stickyglue-atc-label]');
    if (lbl) lbl.textContent = busy ? 'ADDING…' : (btn.getAttribute('data-original-label') || 'ADD TO CART');
  }

  async function addBundleToCart() {
    let items;
    try {
      items = buildItemsForCurrentTier();
    } catch (e) {
      showError(e.message);
      return;
    }

    setBusy(true);
    try {
      const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!res.ok) {
        const errText = await res.text();
        console.warn('[stickyglue-bundle] add failed', res.status, errText);
        showError('Could not add to cart. Please try again.');
        return;
      }
      await refreshCartUI();
      openCartDrawer();
    } catch (err) {
      console.error('[stickyglue-bundle] add error', err);
      showError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function refreshCartUI() {
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      replaceSection('CartDrawer', data['cart-drawer'], '#CartDrawer');
      replaceSection('cart-icon-bubble', data['cart-icon-bubble'], '#cart-icon-bubble');
      const drawerWrap = document.querySelector('cart-drawer');
      if (drawerWrap) drawerWrap.classList.toggle('is-empty', false);
    } catch (e) {
      console.warn('[stickyglue-bundle] refresh failed', e);
    }
  }

  function replaceSection(targetId, html, selector) {
    if (!html) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const src = parsed.querySelector(selector) || parsed.querySelector('#' + targetId);
    if (src) target.innerHTML = src.innerHTML;
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const cartLink = document.querySelector('a[href="/cart"]');
    if (cartLink) window.location.href = '/cart';
  }

  function init() {
    root = document.querySelector('[data-stickyglue-bundle]');
    if (!root) return;

    document.body.classList.add('stickyglue-bundle-active');

    const atcEl = root.querySelector('[data-stickyglue-atc]');
    const lbl = atcEl && atcEl.querySelector('[data-stickyglue-atc-label]');
    if (atcEl && lbl) atcEl.setAttribute('data-original-label', (lbl.textContent || '').trim());

    const settingDefault = atcEl && atcEl.getAttribute('data-default-tier');
    const checkedRadio = root.querySelector('input.stickyglue-tier__radio:checked');
    const initialTier = parseInt(
      settingDefault || (checkedRadio && checkedRadio.value) || '1',
      10
    );
    state.tier = (initialTier >= 1 && initialTier <= 3) ? initialTier : 1;

    const subCb = root.querySelector('[data-stickyglue-subscribe]');
    state.subscribe = subCb ? !!subCb.checked : true;

    attachTierListeners();
    attachSubscribeListener();
    renderTierSelection();
    syncSubscribeClass();

    if (atcEl) atcEl.addEventListener('click', addBundleToCart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
